import { H5 } from "@sellar-io/components";
import { StatementListResponseType } from "@sellar-io/api-v2/types";
import { formatCurrency } from "@sellar-io/common";
import dayjs from "dayjs";
import {
  Icon,
  ListElement,
  ProfilePicture,
} from "../../../../../../components";

import { Badge } from "../../../../../../components/badge/Badge";
import { InfiniteList } from "../../../../../../components/infiniteLoading/InfiniteList";
import { twMerge } from "tailwind-merge";
import { StatementPaymentStatusBadge } from "./StatementPaymentStatusBadge";
import { StatementDownloadButton } from "./StatementDownloadButton";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";

const SellarCreditStatementCard = ({
  statement,
}: {
  statement: StatementListResponseType[number];
}) => {
  return (
    <ListElement>
      <ProfilePicture
        logoUrl={statement.Supplier.logoUrl}
        name={statement.Supplier.name}
        size="large"
      />
      <div className={"flex w-full flex-col gap-1"}>
        <span className="text-xs text-zinc-400">
          Created {dayjs(statement.createdAt).format("DD/MM/YYYY")}
        </span>
        <H5>
          {statement.Supplier.name}
          <span className={statement.type === "CN" ? "text-emerald-700" : ""}>
            {statement.type === "INV" ? " - " : " (+) "}
            {formatCurrency(statement.total)}
          </span>
        </H5>
        <span className="text-sm">
          {statement.Business.name}
          {!!statement.orderPlacedByName && ` • ${statement.orderPlacedByName}`}
        </span>
        <div className="mt-2 flex gap-2">
          <Badge
            className={twMerge(
              "flex items-center gap-1 text-sm",
              statement.type === "CN" && "bg-emerald-100 text-emerald-700",
            )}
          >
            <Icon
              icon={
                statement.type === "INV"
                  ? "FILE_DOCUMENT_OUTLINE"
                  : "NOTE_EDIT_OUTLINE"
              }
            />
            {statement.customId}
          </Badge>
          {statement.type === "INV" && (
            <StatementPaymentStatusBadge statement={statement} />
          )}
        </div>
      </div>
      <StatementDownloadButton document={statement} className="" />
    </ListElement>
  );
};

export const SellarCreditStatementCardList = ({
  query,
}: {
  query: UseInfiniteQueryResult<InfiniteData<StatementListResponseType>, Error>;
}) => {
  const statements =
    query.data?.pages.flatMap((page) => {
      return page.map((page) => page);
    }) ?? [];

  return (
    <>
      <InfiniteList query={query}>
        {statements.map((statement, idx) => (
          <SellarCreditStatementCard key={idx} statement={statement} />
        ))}
      </InfiniteList>
      {statements.length === 0 && (
        <div className="w-full p-4">
          <span className="text-sm">Your search returned 0 results</span>
        </div>
      )}
    </>
  );
};
